import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listAdjustments, AdjustmentRow } from '../../../../services/inventoryApi';
import { AsyncBoundary } from '../../../../components/AsyncBoundary';
import styles from './BatchExpiryTab.module.css';

/**
 * Read-only ledger of recent inventory adjustments (human + system).
 * System rows (reservation auto-corrections) have a null recorder.
 */
export const AdjustmentsTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<AdjustmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listAdjustments(100));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load adjustments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      r.sku_code.toLowerCase().includes(q) ||
      r.product_name.toLowerCase().includes(q) ||
      r.lot_number.toLowerCase().includes(q) ||
      r.reason_code.toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div>
      <div className={styles.subHeader}>
        <p className={styles.subtitle}>Stock adjustment ledger — every manual correction and system reservation</p>
        <div className={styles.subActions}>
          <button type="button" className={styles.fullInvBtn} onClick={fetchData} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className={styles.filterRow}>
        <div />
        <div className={styles.searchBox}>
          <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input className={styles.searchInput} placeholder="Search SKU, lot, reason..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <AsyncBoundary
        loading={loading && rows.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="No adjustments recorded yet"
        onRetry={fetchData}
      >
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>DATE</th>
                <th>SKU</th>
                <th>PRODUCT</th>
                <th>LOT</th>
                <th>LOCATION</th>
                <th>CHANGE</th>
                <th>REASON</th>
                <th>BY</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  <td className={styles.skuCell}>{r.sku_code}</td>
                  <td>{r.product_name}</td>
                  <td className={styles.idCell}>{r.lot_number}</td>
                  <td>{r.location_code}</td>
                  <td className={styles.bold} style={{ color: r.quantity_change < 0 ? '#DC2626' : '#16A34A' }}>
                    {r.quantity_change > 0 ? `+${r.quantity_change}` : r.quantity_change}
                  </td>
                  <td>{r.reason_code}</td>
                  <td>{r.recorded_by_name || 'System / Reservation'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AsyncBoundary>
    </div>
  );
};
